import { apiFetch } from "@/client/lib/api";

/**
 * Typed client for the node BMC endpoints (PE-650). The portal authenticates and
 * tenant-scopes every call, then reverse-proxies to the internal
 * customer-bmc-proxy keyed by the node. The browser never talks to a BMC or the
 * proxy directly. Power is tenant-admin only; the policy/redfish reads are
 * staff-only (active "view as") and enforced server-side.
 */

export type PowerAction =
  | "PowerOn"
  | "GracefulShutdown"
  | "PowerOff"
  | "GracefulRestart"
  | "PowerReset";

/** A node's current power + identity snapshot. */
export interface BmcSummary {
  powerState: string | null;
  model: string | null;
  serialNumber: string | null;
  name: string | null;
  health: string | null;
  state: string | null;
}

/** A single rule in the proxy's default-deny Redfish policy. */
export interface BmcPolicyRule {
  name: string;
  effect: "allow" | "deny";
  methods?: string[];
  path?: string;
  pathPrefix?: string;
  pathPattern?: string;
}

export interface BmcPolicy {
  default: string;
  rules: BmcPolicyRule[];
}

/** Raw Redfish passthrough result for the staff explorer. */
export interface BmcRedfishResult {
  status: number;
  body: string;
}

export const bmcApi = {
  summary: (nodeId: string) => apiFetch<BmcSummary>(`/nodes/${nodeId}/bmc/summary`),
  power: (nodeId: string, action: PowerAction) =>
    apiFetch<{ ok: boolean; action: PowerAction }>(`/nodes/${nodeId}/bmc/power`, {
      method: "POST",
      json: { action },
    }),
  policy: (nodeId: string) => apiFetch<BmcPolicy>(`/nodes/${nodeId}/bmc/policy`),
  redfish: (nodeId: string, path: string) =>
    apiFetch<BmcRedfishResult>(`/nodes/${nodeId}/bmc/redfish?path=${encodeURIComponent(path)}`),
};
